import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import Swal from 'sweetalert2';
import { FaEdit, FaPlus, FaBan, FaCheckCircle, FaTrash, FaTag } from 'react-icons/fa';
import { fetchAuth, fetchJson } from '../../../services/apiClient';

const Page = styled.div`padding: 32px; min-width: 0; @media(max-width:700px){padding:18px 12px;}`;
const Header = styled.div`display:flex;justify-content:space-between;align-items:center;gap:16px;flex-wrap:wrap;margin-bottom:24px;`;
const Button = styled.button`border:0;border-radius:8px;padding:10px 16px;background:#34574a;color:white;cursor:pointer;font-weight:600;display:inline-flex;align-items:center;gap:8px;&:disabled{opacity:.55;cursor:not-allowed;}`;
const Secondary = styled(Button)`background:#ba824f;`;
const Danger = styled(Button)`background:#b42318;`;
const Card = styled.div`background:#fff;border-radius:12px;box-shadow:0 2px 8px rgba(0,0,0,.08);overflow:auto;`;
const Table = styled.table`width:100%;border-collapse:collapse;min-width:900px;th,td{padding:14px;text-align:left;border-bottom:1px solid #eee;}th{background:#f7f8f8;color:#52605b;font-size:13px;}small{display:block;color:#718078;margin-top:3px;}`;
const Status = styled.span`display:inline-block;padding:4px 10px;border-radius:999px;font-size:12px;font-weight:700;background:${p=>p.$bg};color:${p=>p.$color};`;
const Modal = styled.div`position:fixed;inset:0;background:rgba(0,0,0,.38);z-index:1200;display:flex;align-items:center;justify-content:center;padding:18px;`;
const Form = styled.form`background:#fff;border-radius:14px;padding:26px;width:min(680px,100%);max-height:90vh;overflow:auto;display:grid;grid-template-columns:1fr 1fr;gap:16px;h2{grid-column:1/-1;margin:0 0 4px;}label{display:flex;flex-direction:column;gap:7px;font-size:14px;color:#34443e;}input,select,textarea{width:100%;min-width:0;padding:11px;border:1px solid #d8dfdc;border-radius:7px;background:white;font-family:inherit;} .wide{grid-column:1/-1}.actions{grid-column:1/-1;display:flex;justify-content:flex-end;gap:10px;margin-top:8px}@media(max-width:620px){grid-template-columns:1fr;.wide,.actions,h2{grid-column:1}}`;
const Hint = styled.p`grid-column:1/-1;margin:0;font-size:13px;color:#718078;`;

const initial = {
  nomePROMOCAO: '',
  dataInicioPROMOCAO: '',
  dataFimPROMOCAO: '',
  totalPermitidoPROMOCAO: 100,
  aplicaProfissional12mPROMOCAO: true,
  aplicaFeriasPROMOCAO: true,
  tituloExpiradoPROMOCAO: '',
  mensagemExpiradoPROMOCAO: '',
  ativoPROMOCAO: true,
};

// DATETIME do MySQL não guarda timezone — tratar sempre como horário local
const toInputValue = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d)) return '';
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

const fromInputValue = (value) => (value ? `${value.replace('T', ' ')}:00` : null);

const fmtDateTime = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  return isNaN(d) ? '—' : d.toLocaleString('pt-PT', { dateStyle: 'short', timeStyle: 'short' });
};

function statusOf(p, agora = new Date()) {
  if (!p.ativoPROMOCAO) return { label: 'Inativa', bg: '#f1f1f1', color: '#718078' };
  const ini = new Date(p.dataInicioPROMOCAO);
  const fim = new Date(p.dataFimPROMOCAO);
  if (agora < ini) return { label: 'Agendada', bg: '#e8f0fe', color: '#1a56db' };
  if (agora >= fim) return { label: 'Expirada', bg: '#fdecea', color: '#b42318' };
  return { label: 'Ativa', bg: '#e6f4ea', color: '#1e7e34' };
}

export default function PromotionManager() {
  const [promocoes, setPromocoes] = useState([]);
  const [ativaInfo, setAtivaInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(initial);
  const [showForm, setShowForm] = useState(false);

  const request = async (path, options = {}) => {
    const { res, data } = await fetchAuth(`/api/promocoes${path}`, options);
    if (!res.ok) throw new Error(data?.error || data?.mensagem || 'Não foi possível concluir a operação.');
    return data;
  };

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [lista, ativa] = await Promise.all([
        request(''),
        fetchJson('/api/promocoes/ativa').then(({ data }) => data).catch(() => null),
      ]);
      setPromocoes(Array.isArray(lista) ? lista : []);
      setAtivaInfo(ativa);
    } catch (e) {
      Swal.fire('Erro', e.message, 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const open = (p = null) => {
    setEditing(p);
    setForm(p ? {
      nomePROMOCAO: p.nomePROMOCAO || '',
      dataInicioPROMOCAO: toInputValue(p.dataInicioPROMOCAO),
      dataFimPROMOCAO: toInputValue(p.dataFimPROMOCAO),
      totalPermitidoPROMOCAO: p.totalPermitidoPROMOCAO ?? 100,
      aplicaProfissional12mPROMOCAO: !!p.aplicaProfissional12mPROMOCAO,
      aplicaFeriasPROMOCAO: !!p.aplicaFeriasPROMOCAO,
      tituloExpiradoPROMOCAO: p.tituloExpiradoPROMOCAO || '',
      mensagemExpiradoPROMOCAO: p.mensagemExpiradoPROMOCAO || '',
      ativoPROMOCAO: !!p.ativoPROMOCAO,
    } : { ...initial });
    setShowForm(true);
  };

  const close = () => { setShowForm(false); setEditing(null); setForm(initial); };

  const change = (e) => setForm(v => ({ ...v, [e.target.name]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        dataInicioPROMOCAO: fromInputValue(form.dataInicioPROMOCAO),
        dataFimPROMOCAO: fromInputValue(form.dataFimPROMOCAO),
        totalPermitidoPROMOCAO: Number(form.totalPermitidoPROMOCAO) || 0,
      };
      await request(editing ? `/${editing.codigoPROMOCAO}` : '', {
        method: editing ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      close();
      await load();
      Swal.fire('Sucesso', 'Promoção guardada.', 'success');
    } catch (err) {
      Swal.fire('Erro', err.message, 'error');
    }
  };

  const toggle = async (p) => {
    try {
      await request(`/${p.codigoPROMOCAO}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ativoPROMOCAO: !p.ativoPROMOCAO }),
      });
      await load();
    } catch (e) {
      Swal.fire('Erro', e.message, 'error');
    }
  };

  const remove = async (p) => {
    const result = await Swal.fire({
      title: 'Excluir promoção?',
      text: `"${p.nomePROMOCAO}" será removida definitivamente.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Excluir',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#b42318',
    });
    if (!result.isConfirmed) return;
    try {
      await request(`/${p.codigoPROMOCAO}`, { method: 'DELETE' });
      await load();
    } catch (e) {
      Swal.fire('Erro', e.message, 'error');
    }
  };

  return <Page>
    <Header>
      <div>
        <h1><FaTag style={{ marginRight: 8 }} />Promoções</h1>
        <p>Campanhas da página de planos: cronómetro, vagas e mensagens de urgência.</p>
      </div>
      <Button onClick={() => open()}><FaPlus /> Nova promoção</Button>
    </Header>

    {ativaInfo?.promocao && (
      <Card style={{ padding: 16, marginBottom: 20 }}>
        <strong>Em vigor agora:</strong> {ativaInfo.promocao.nomePROMOCAO}
        {' · '}termina {fmtDateTime(ativaInfo.promocao.dataFimPROMOCAO)}
        {' · '}vagas restantes: {ativaInfo.uso?.totalRestante ?? '—'} de {ativaInfo.uso?.totalPermitido ?? '—'}
        {ativaInfo.encerrado && ' · '} {ativaInfo.encerrado && <Status $bg="#fdecea" $color="#b42318">Encerrada (modal "tempo acabou")</Status>}
      </Card>
    )}

    <Card>
      <Table>
        <thead>
          <tr><th>Nome</th><th>Início</th><th>Fim</th><th>Vagas</th><th>Abas</th><th>Estado</th><th>Ações</th></tr>
        </thead>
        <tbody>
          {loading ? <tr><td colSpan="7">A carregar...</td></tr>
            : promocoes.length === 0 ? <tr><td colSpan="7">Nenhuma promoção cadastrada.</td></tr>
            : promocoes.map(p => {
              const st = statusOf(p);
              const abas = [
                p.aplicaProfissional12mPROMOCAO && 'Profissional 12m',
                p.aplicaFeriasPROMOCAO && 'Férias',
              ].filter(Boolean).join(', ') || '—';
              return <tr key={p.codigoPROMOCAO}>
                <td>{p.nomePROMOCAO}<small>ID {p.codigoPROMOCAO}</small></td>
                <td>{fmtDateTime(p.dataInicioPROMOCAO)}</td>
                <td>{fmtDateTime(p.dataFimPROMOCAO)}</td>
                <td>{p.totalPermitidoPROMOCAO}</td>
                <td>{abas}</td>
                <td><Status $bg={st.bg} $color={st.color}>{st.label}</Status></td>
                <td>
                  <Button onClick={() => open(p)} title="Editar"><FaEdit /></Button>{' '}
                  {p.ativoPROMOCAO
                    ? <Secondary onClick={() => toggle(p)} title="Desativar"><FaBan /></Secondary>
                    : <Secondary onClick={() => toggle(p)} title="Ativar"><FaCheckCircle /></Secondary>}{' '}
                  <Danger onClick={() => remove(p)} title="Excluir"><FaTrash /></Danger>
                </td>
              </tr>;
            })}
        </tbody>
      </Table>
    </Card>

    {showForm && <Modal onMouseDown={e => { if (e.target === e.currentTarget) close(); }}>
      <Form onSubmit={save}>
        <h2>{editing ? 'Editar promoção' : 'Nova promoção'}</h2>
        <Hint>As datas são interpretadas no horário local do servidor.</Hint>
        <label className="wide">Nome<input name="nomePROMOCAO" value={form.nomePROMOCAO} onChange={change} required placeholder="Ex.: Campanha de lançamento" /></label>
        <label>Início<input name="dataInicioPROMOCAO" type="datetime-local" value={form.dataInicioPROMOCAO} onChange={change} required /></label>
        <label>Fim<input name="dataFimPROMOCAO" type="datetime-local" value={form.dataFimPROMOCAO} onChange={change} required /></label>
        <label>Vagas disponíveis<input name="totalPermitidoPROMOCAO" type="number" min="1" value={form.totalPermitidoPROMOCAO} onChange={change} required /></label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 7, fontSize: 14, color: '#34443e' }}>
          Mostrar urgência em
          <label style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}><input style={{ width: 'auto' }} name="aplicaProfissional12mPROMOCAO" type="checkbox" checked={form.aplicaProfissional12mPROMOCAO} onChange={change} /> Profissional (12 meses)</label>
          <label style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}><input style={{ width: 'auto' }} name="aplicaFeriasPROMOCAO" type="checkbox" checked={form.aplicaFeriasPROMOCAO} onChange={change} /> Férias</label>
        </div>
        <label className="wide">Título do modal "tempo esgotado" (opcional)<input name="tituloExpiradoPROMOCAO" value={form.tituloExpiradoPROMOCAO} onChange={change} placeholder="O tempo acabou!" /></label>
        <label className="wide">Mensagem do modal "tempo esgotado" (opcional)<textarea name="mensagemExpiradoPROMOCAO" rows="4" value={form.mensagemExpiradoPROMOCAO} onChange={change} placeholder={'FOI PENA TERES PERDIDO ESTA COMPRA\nCOM ESTES PREÇOS, MAS BREVEMENTE,\nTEREMOS MAIS NOVIDADES.\nFICA ATENTO'} /></label>
        <label className="wide"><span><input style={{ width: 'auto', marginRight: 8 }} name="ativoPROMOCAO" type="checkbox" checked={form.ativoPROMOCAO} onChange={change} /> Promoção ativa</span></label>
        <div className="actions">
          <Secondary type="button" onClick={close}>Cancelar</Secondary>
          <Button type="submit">Guardar</Button>
        </div>
      </Form>
    </Modal>}
  </Page>;
}
